import {
  createContext,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import { CustomTree } from "./CustomTree";
import { CustomersFilter } from "../../../../core/hooks/filters/useCustomersFilter";
import useTranslations from "../../../../core/i18n/useTranslations";
import Icon from "../../Icon";
import {
  getOrganizationNames,
  getOrganizationTrees,
} from "../../../../core/api/organizations/organizations";
import useUser from "../../../../core/user/useUser";
import { transformOrganizationsToTreeData } from "../../../../core/utils/organizations";
import { useClearDropdownEffect } from "../../../../core/hooks/filters/useClearDropdownEffect";

import styles from "./CustomTreeDropdown.module.css";
import inputStyles from "../../Inputs/Input.module.css";

export const defaultPageSettings = {
  page: 1,
  pageSize: 100,
};

export interface ITreeData {
  key?: string | number;
  label?: string;
  data?: any;
  icon?: string;
  children?: ITreeData[];
  style?: object;
  className?: string;
  droppable?: boolean;
  draggable?: boolean;
  selectable?: boolean;
  leaf?: boolean;
  hasSelectedChild?: boolean;
  requireCostCenter?: boolean;
  parentId?: number | null;
  hasChildren?: boolean;
}

interface ITreeContextProps {
  items: ITreeData[];
  selectedItems: string[];
  customerFilter: { customers?: string };
  selectionMode?: "multiple" | "single";
  setSelectedItems: (items: string[]) => void;
  handleSelect: (element: ITreeData | ITreeData[], toSelect?: boolean) => void;
  isLoading: boolean;
  canFetchMore?: boolean;
  fetchOrganizations: () => Promise<void>;
  abortController: AbortController | null;
}

export const TreeContext = createContext<ITreeContextProps>({
  items: [],
  selectedItems: [],
  customerFilter: { customers: "" },
  selectionMode: "multiple",
  setSelectedItems: () => {},
  handleSelect: () => {},
  canFetchMore: false,
  isLoading: false,
  fetchOrganizations: async () => {},
  abortController: null,
});

export const CustomDropdown = memo(
  ({
    customerFilter,
    selectionMode = "multiple",
    onSelect,
    onSelectSignle,
    disabled = false,
    validationError,
    fullWidth = false,
    placeholder,
    label,
    required = false,
    hideLabel = false,
    parentId,
    itemsToShow = 8,
  }: {
    customerFilter?: CustomersFilter;
    selectionMode?: "multiple" | "single";
    onSelect?: (ids: string) => void;
    onSelectSignle?: (item?: ITreeData) => void;
    disabled?: boolean;
    validationError?: string;
    fullWidth?: boolean;
    placeholder?: string;
    label?: string;
    required?: boolean;
    hideLabel?: boolean;
    parentId?: string;
    itemsToShow?: number;
  }) => {
    const t = useTranslations();
    const { authenticatedRequest } = useUser();
    const [openPopup, setOpenPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [needToLoadNames, setNeedToLoadNames] = useState(true);
    const [selectedItems, setSelectedItems] = useState<string[]>(
      customerFilter?.customers?.split(",") ?? []
    );
    const [selectedValues, setSelectedValues] = useState<string[]>();
    const [organizations, setOrganizations] = useState<ITreeData[]>();
    const [canFetchMore, setCanFetchMore] = useState(true);
    const inputRef = useRef<HTMLButtonElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // refs to avoid closure issues in async functions
    const pageSettingsRef = useRef(defaultPageSettings);
    const organizationsRef = useRef(organizations);
    const searchValueRef = useRef("");

    const dropdownId = uuidv4();

    useEffect(() => {
      organizationsRef.current = organizations;
    }, [organizations]);

    const fetchOrganizations = async () => {
      try {
        setIsLoading(true);
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const response = await getOrganizationTrees(
          {
            page: pageSettingsRef.current.page,
            pageSize: pageSettingsRef.current.pageSize,
            nested: true,
            search: searchValueRef.current,
            parentId: parentId ?? undefined,
            includeParent: parentId ? true : undefined,
          },
          authenticatedRequest,
          abortControllerRef.current.signal
        );
        if (pageSettingsRef.current.page === 1) {
          setOrganizations(
            transformOrganizationsToTreeData(response.data.organizations)
          );
        } else {
          setOrganizations((prev) => [
            ...(prev ?? []),
            ...transformOrganizationsToTreeData(response.data.organizations),
          ]);
        }
        pageSettingsRef.current = {
          ...pageSettingsRef.current,
          page: pageSettingsRef.current.page + 1,
        };
        setCanFetchMore(
          response.data.total > response.data.page * response.data.pageSize
        );
      } finally {
        setIsLoading(false);
      }
    };

    const fetchOrganizationNames = async () => {
      try {
        setIsLoading(true);
        const response = await getOrganizationNames(
          customerFilter?.customers ?? "",
          authenticatedRequest
        );
        setSelectedValues(response.data ?? []);
        setNeedToLoadNames(false);
      } finally {
        setIsLoading(false);
      }
    };

    const gatherValuesRecursively = (
      setValues: string[],
      arr: string[] = [],
      itemsToProcess = organizations
    ) => {
      (itemsToProcess ?? []).forEach((item) => {
        if (item.key && setValues.includes(item.key.toString())) {
          arr.push(item?.label ?? "");
        }
        if (item.children?.length) {
          gatherValuesRecursively(setValues, arr, item.children);
        }
      });
      return arr;
    };

    useClearDropdownEffect({
      customerFilter,
      searchValueRef,
      pageSettingsRef,
      fetchOrganizations,
      openPopup,
    });

    useEffect(() => {
      if (openPopup && !organizations?.length && !isLoading) {
        fetchOrganizations();
      }
    }, [openPopup]);

    useEffect(() => {
      if (selectedItems.length && !selectedValues?.length && needToLoadNames) {
        fetchOrganizationNames();
      }
      if (openPopup && !needToLoadNames) {
        const setValues = [...selectedItems];
        const values = gatherValuesRecursively(setValues);
        setSelectedValues(values);
        if (setValues.length !== selectedValues?.length) {
          setSelectedItems(setValues);
        }

        if (selectionMode === "single") {
          setOpenPopup(false);
        }
      }
    }, [selectedItems]);

    useEffect(() => {
      if (
        !customerFilter?.customers &&
        selectedItems.length &&
        selectedValues?.length &&
        !openPopup
      ) {
        setSelectedItems([]);
        setSelectedValues([]);
      }
    }, [customerFilter?.customers]);

    const handleSelect = (
      element: ITreeData | ITreeData[],
      toSelect?: boolean
    ) => {
      const currentSelected = [...selectedItems];
      if (selectionMode === "multiple") {
        if (!Array.isArray(element)) {
          if (!element.key) return;
          const elementKey = element.key.toString();
          if (!currentSelected.includes(elementKey)) {
            currentSelected.push(elementKey);
          } else {
            const index = currentSelected.indexOf(elementKey);
            if (index > -1) {
              currentSelected.splice(index, 1);
            }
          }
        } else {
          element.forEach((item) => {
            if (!item.key) return;
            const elementKey = item.key.toString();
            if (!currentSelected.includes(elementKey) && toSelect) {
              currentSelected.push(item.key.toString());
            }
            if (currentSelected.includes(elementKey) && !toSelect) {
              const index = currentSelected.indexOf(elementKey);
              if (index > -1) {
                currentSelected.splice(index, 1);
              }
            }
          });
        }
        setSelectedItems(currentSelected);
        onSelect?.(currentSelected.join(","));
      } else {
        const selectedItem = element as ITreeData;
        if (currentSelected.includes(selectedItem.key!.toString())) {
          currentSelected.pop();
        } else {
          currentSelected.length = 0;
          currentSelected.push(selectedItem.key!.toString());
        }
        setSelectedItems(currentSelected);
        onSelect?.(currentSelected[0]);
        onSelectSignle?.(currentSelected[0] ? selectedItem : undefined);
      }
    };

    return (
      <TreeContext.Provider
        value={useMemo(
          () => ({
            items: organizations ?? [],
            selectedItems,
            customerFilter: customerFilter ?? { customers: "" },
            selectionMode,
            setSelectedItems,
            handleSelect,
            canFetchMore,
            isLoading,
            fetchOrganizations,
            abortController: abortControllerRef.current,
          }),
          [
            organizations,
            selectedItems,
            customerFilter,
            selectionMode,
            canFetchMore,
            isLoading,
          ]
        )}
      >
        <div
          id={dropdownId}
          className={classNames({
            [styles.customDropdownInputWrapper]: true,
            ["w-100"]: fullWidth,
          })}
          data-cy="organisation-filter-button"
        >
          {label && (
            <>
              {required && <span className={inputStyles.requiredIcon}>*</span>}
              <label
                htmlFor={dropdownId}
                className={hideLabel ? "visuallyHidden" : ""}
              >
                {label}
              </label>
            </>
          )}
          <button
            id={dropdownId}
            className={classNames(styles.customDropdownInput, {
              [styles.fullWidth]: fullWidth,
              [styles.error]: !!validationError,
            })}
            disabled={disabled}
            ref={inputRef}
            onClick={() => {
              setOpenPopup(!openPopup);
              if (needToLoadNames) {
                setNeedToLoadNames(false);
              }
            }}
            type="button"
          >
            <span
              className={classNames({
                [styles.label]: !selectedValues?.length,
                [styles.selectedValuesLabel]: !!selectedValues?.length,
              })}
            >
              {selectedValues?.length
                ? selectedValues.join(",")
                : placeholder ?? `--${t("Common:organisation")}--`}
            </span>
            <Icon name={openPopup ? "chevron-up" : "chevron-down"} size={14} />
          </button>
          {validationError ? (
            <div className={styles.validationError}>
              <Icon name="exclamation-circle" size={14} />
              {validationError}
            </div>
          ) : null}
          {openPopup &&
            createPortal(
              <CustomTree
                onClose={() => {
                  setOpenPopup(false);
                }}
                parentRef={inputRef}
                searchValueRef={searchValueRef}
                pageSettingsRef={pageSettingsRef}
                itemsToShow={itemsToShow}
              />,
              document.body
            )}
        </div>
      </TreeContext.Provider>
    );
  }
);
